import { useCallback, useEffect, useReducer } from 'react';

import { cfetch } from '@confluence/network';

const LOOM_VIDEO_THUMBNAIL_OPERATION = 'LoomVideoThumbnailHydration';

const LOOM_VIDEO_THUMBNAIL_QUERY = `
	query LoomVideoThumbnailHydration($ids: [ID!]!) {
		loom_videos(ids: $ids) {
			id
			thumbnails {
				default
				static
			}
		}
	}
`;

type LoomVideoThumbnailResponse = {
	data?: {
		loom_videos?: Array<{
			id: string;
			thumbnails: {
				default: string | null;
				static: string | null;
			} | null;
		}> | null;
	};
	errors?: Array<{ message?: string }>;
};

export type ThumbnailMap = Map<string, string>;

/**
 * Returns true if the ARI belongs to a video entity (e.g. Loom).
 * Video ARIs from Data Depot follow the pattern: ari:cloud:graph::video/...
 */
function isVideoAri(ari: string): boolean {
	return ari.includes('::video/');
}

/**
 * Extracts the Loom video ID from a loom.com share URL.
 * e.g. "https://www.loom.com/share/abc123" → "abc123"
 */
function extractLoomVideoId(url: string): string | null {
	try {
		const parsed = new URL(url);
		if (!parsed.hostname.includes('loom.com')) {
			return null;
		}
		const parts = parsed.pathname.split('/');
		const shareIndex = parts.indexOf('share');
		if (shareIndex >= 0 && shareIndex + 1 < parts.length) {
			return parts[shareIndex + 1];
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Constructs a proper Loom video ARI from its components.
 * Format: ari:cloud:loom:{cloudId}:video/activation/{activationId}/{videoId}
 */
function buildLoomAri(cloudId: string, activationId: string, videoId: string): string {
	return `ari:cloud:loom:${cloudId}:video/activation/${activationId}/${videoId}`;
}

/**
 * Fetch fresh Loom video thumbnails directly from AGG's loom_videos query.
 * The suggestions API stores Loom thumbnail URLs from Data Depot with signed tokens
 * that are expired by the time we render them. This function queries AGG's loom_videos
 * field directly to get fresh signed URLs, similar to how SmartLinks resolves Loom thumbnails.
 */
async function fetchLoomVideoThumbnails(
	entityIdToUrl: Map<string, string>,
	cloudId: string,
	activationId: string,
): Promise<{ thumbnails: ThumbnailMap } | { error: Error }> {
	const loomAriToEntityId = new Map<string, string>();
	for (const [entityId, url] of entityIdToUrl) {
		const loomVideoId = extractLoomVideoId(url);
		if (loomVideoId) {
			const loomAri = buildLoomAri(cloudId, activationId, loomVideoId);
			loomAriToEntityId.set(loomAri, entityId);
		}
	}

	if (loomAriToEntityId.size === 0) {
		return { thumbnails: new Map() };
	}

	const loomAris = [...loomAriToEntityId.keys()];

	try {
		const response = await cfetch(`/gateway/api/graphql?q=${LOOM_VIDEO_THUMBNAIL_OPERATION}`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				query: LOOM_VIDEO_THUMBNAIL_QUERY,
				variables: { ids: loomAris },
				operationName: LOOM_VIDEO_THUMBNAIL_OPERATION,
			}),
		});

		const json = (await response.json()) as LoomVideoThumbnailResponse;

		if (json.errors?.length) {
			return {
				error: new Error(json.errors.map((e) => e.message ?? 'GraphQL error').join('; ')),
			};
		}

		const thumbnails: ThumbnailMap = new Map();

		// Build a reverse lookup from video ID suffix → entity ID for fallback matching.
		// The returned ARI may have a different activationId than what we sent.
		const videoIdToEntityId = new Map<string, string>();
		const loomAriEntries = Array.from(loomAriToEntityId.entries());
		for (let i = 0; i < loomAriEntries.length; i++) {
			const [loomAri, graphEntityId] = loomAriEntries[i];
			const lastSlash = loomAri.lastIndexOf('/');
			if (lastSlash >= 0) {
				videoIdToEntityId.set(loomAri.substring(lastSlash + 1), graphEntityId);
			}
		}

		const videos = json.data?.loom_videos ?? [];
		for (let i = 0; i < videos.length; i++) {
			const video = videos[i];
			const thumbnailUrl = video.thumbnails?.static ?? video.thumbnails?.default;
			if (!video.id || !thumbnailUrl) {
				continue;
			}
			let entityId = loomAriToEntityId.get(video.id);
			if (!entityId) {
				const lastSlash = video.id.lastIndexOf('/');
				const returnedVideoId = lastSlash >= 0 ? video.id.substring(lastSlash + 1) : '';
				entityId = videoIdToEntityId.get(returnedVideoId);
			}
			if (entityId) {
				thumbnails.set(entityId, thumbnailUrl);
			}
		}

		return { thumbnails };
	} catch (e) {
		return {
			error: e instanceof Error ? e : new Error('Failed to fetch Loom video thumbnails'),
		};
	}
}

export type UseLoomThumbnailsResult = {
	thumbnailsByEntityId: ThumbnailMap;
	isLoading: boolean;
	error: Error | null;
};

/**
 * Hook to hydrate fresh Loom video thumbnails for space curation suggestions.
 *
 * Loom video thumbnail URLs from the suggestions API (via Data Depot) have signed tokens
 * that are expired by render time. This hook queries AGG's loom_videos field directly
 * to get fresh signed URLs.
 *
 * Google Drive document thumbnails are not hydrated here — they use the iconUrl from the
 * suggestions API directly.
 */
export const useLoomThumbnails = (
	suggestions: Array<{ id: string; url: string }>,
	cloudId: string,
	activationId: string,
): UseLoomThumbnailsResult => {
	type State = { thumbnailsByEntityId: ThumbnailMap; isLoading: boolean; error: Error | null };
	const [state, dispatch] = useReducer((_prev: State, next: State) => next, {
		thumbnailsByEntityId: new Map(),
		isLoading: false,
		error: null,
	});

	// Build a stable key from video entity IDs
	const videoEntries: Array<[string, string]> = [];
	for (const s of suggestions) {
		if (isVideoAri(s.id)) {
			videoEntries.push([s.id, s.url]);
		}
	}
	const stableKey = JSON.stringify(videoEntries);

	const fetchThumbnails = useCallback(async () => {
		const entries: Array<[string, string]> = JSON.parse(stableKey);
		if (entries.length === 0) {
			return;
		}

		const entityIdToUrl = new Map(entries);
		const result = await fetchLoomVideoThumbnails(entityIdToUrl, cloudId, activationId);
		const hasError = 'error' in result;
		const thumbnails = hasError ? new Map<string, string>() : result.thumbnails;

		// Preload images so they render instantly when the component uses the URL
		const preloadPromises: Array<Promise<void>> = [];
		for (const [, url] of thumbnails) {
			preloadPromises.push(
				new Promise<void>((resolve) => {
					const img = new Image();
					img.onload = () => resolve();
					img.onerror = () => resolve();
					img.src = url;
				}),
			);
		}
		await Promise.all(preloadPromises);

		dispatch({
			thumbnailsByEntityId: thumbnails,
			isLoading: false,
			error: hasError ? result.error : null,
		});
	}, [stableKey, cloudId, activationId]);

	useEffect(() => {
		void fetchThumbnails();
	}, [fetchThumbnails]);

	return state;
};
